//@ts-nocheck
import React, { forwardRef, HTMLAttributes, ReactNode } from 'react';
import { useFocus, useFocusWithin } from '@react-aria/interactions';
import { AriaTextFieldOptions } from '@react-aria/textfield';
import { mergeProps, useId } from '@react-aria/utils';
import cn from 'classnames';

import { FocusRing, Label } from '@nw/omega-design-system';
import { getDataMaskAttribute } from '@nw/shared/utils';

import { TextFieldHint } from '../TextFieldHint';

export interface TextBaseFieldProps extends AriaTextFieldOptions<'input'> {
  /** A React ref to attach to the rendered Button */
  ref?: React.ForwardedRef<HTMLInputElement>;
  /** An optional hint to show next to the TextField that describes what this TextField expects */
  hint?: ReactNode;
  leftElement?: React.ComponentType<any>;

  rightElement?: React.ComponentType<any>;

  leftElementContainerClassName?: string;

  rightElementContainerClassName?: string;

  color?: string;

  containerClassName?: string;

  inputContainerClassName?: string;

  inputClassName?: string;

  hintClassName?: string;

  labelClassName?: string;
  focusRingClassName?: string;
  isRequired?: boolean;
  shouldShowHint?: boolean;

  inputProps:
    | React.InputHTMLAttributes<HTMLInputElement>
    | React.TextareaHTMLAttributes<HTMLTextAreaElement>;

  labelProps: React.LabelHTMLAttributes<HTMLLabelElement>;
  descriptionProps?: HTMLAttributes<HTMLElement>;
  errorMessageProps?: HTMLAttributes<HTMLElement>;
  handleKeyDown?: (event: React.KeyboardEvent) => void;
  enableDataMask?: boolean;
}

const TextFieldBase = forwardRef<HTMLInputElement, TextBaseFieldProps>(
  (props: TextBaseFieldProps, forwardedRef) => {
    const {
      autoFocus = false,
      errorMessage,
      hint,
      isDisabled = false,
      label,
      leftElement: LeftElement,
      rightElement: RightElement,
      containerClassName = 'w-full',
      leftElementContainerClassName = '',
      rightElementContainerClassName = '',
      inputContainerClassName = '',
      inputClassName = '',
      hintClassName = 'text-input-hint',
      labelClassName = '',
      isRequired,
      focusRingClassName = '',
      shouldShowHint = true,
      inputProps,
      labelProps,
      onFocus,
      onBlur,
      handleKeyDown = () => {},
      enableDataMask
    } = props;
    const inputRef = forwardedRef;
    const [isFocusWithin, setFocusWithin] = React.useState(false);
    const { focusWithinProps } = useFocusWithin({
      isDisabled,
      onFocusWithinChange: (isFocusWithin) => setFocusWithin(isFocusWithin)
    });
    const { focusProps } = useFocus({
      onBlur: () => {
        // Validation is disabled until the user touches / focuses the field at least once
      },
      onFocusChange: (isFocusWithin) => setFocusWithin(isFocusWithin)
    });

    const hintId = useId();

    return (
      <div className={cn('flex flex-col', containerClassName)}>
        {label ? (
          <Label
            labelProps={labelProps}
            labelClassName={labelClassName}
            isRequired={isRequired}
          >
            {label}
          </Label>
        ) : null}
        <section className={cn('table-cell w-full mt-1')}>
          <FocusRing
            autoFocus={autoFocus}
            within
            focusClassProps={`ring-offset-0 ${
              errorMessage && !isFocusWithin
                ? 'ring-0 border-1 border-red-400'
                : isFocusWithin
                  ? focusRingClassName
                    ? focusRingClassName
                    : `ring ring-blue-200`
                  : ''
            }`}
          >
              {/* @ts-ignore */}   
            <div {...focusWithinProps}
              className={cn(
                'flex grow items-center',
                'border-1 border-blue-gray-300 dark:border-gray-700 bg-base-white rounded-lg',
                'text-sm',
                'overflow-hidden',
                inputContainerClassName,
                {
                  'bg-blue-gray-100 dark:bg-blue-gray-100': isDisabled,
                  'bg-white dark:bg-gray-900 shadow-small1': !isDisabled,
                  'cursor-not-allowed': isDisabled,
                  'border-1 border-red-400 dark:border-red-400 rounded-md bg-base-white':
                    !!errorMessage && !isFocusWithin,
                  [`border-1 border-blue-600 rounded-md`]: isFocusWithin
                }
              )}
            >
              {LeftElement ? (
                <div
                  className={cn(
                    'py-2 pl-14px pr-2',
                    'bg-base-white',
                    'select-none',
                    'flex flex-col justify-center    rounded-md',
                    'text-blue-gray-500 dark:text-gray-100 outline-none',
                    { 'bg-blue-gray-100': isDisabled },
                    leftElementContainerClassName
                  )}
                >
                  <LeftElement
                    color={errorMessage && !isFocusWithin ? '#f87171' : null}
                  />
                </div>
              ) : null}
              <input
                ref={inputRef}
                lens-role="text-field"
                {...(mergeProps(
                  focusProps,
                  inputProps
                ) as React.InputHTMLAttributes<HTMLInputElement>)}
                className={cn(
                  'grow-default input rounded-lg w-full',
                  'px-14px py-2',
                  'focus:border-0 focus:rounded-md focus:outline-none',
                  'text-blue-gray-900 text-base leading-5 tracking-normal not-italic',
                  'placeholder-blue-gray-500',
                  {
                    disabled: isDisabled
                  },
                  {
                    'placeholder-blue-gray-400': isDisabled
                  },
                  {
                    'border-none shadow-none rounded-l-none pl-0': LeftElement
                  },
                  {
                    'border-none rounded-none shadow-none pr-0': RightElement
                  },
                  {
                    'border-none rounded-none shadow-none text-red-800':
                      !!errorMessage && !isFocusWithin
                  },
                  inputClassName
                )}
                required={isRequired}
                {...getDataMaskAttribute(enableDataMask)}
                // @ts-ignore
                onKeyDown={handleKeyDown}
              />

              {RightElement ? (
                <button
                  className={cn(
                    'py-2 pr-14px pl-2',
                    'bg-base-white',
                    'select-none',
                    'flex flex-col justify-center    rounded-md',
                    'text-blue-gray-500 dark:text-gray-100 outline-none',
                    { 'bg-blue-gray-100': isDisabled },
                    rightElementContainerClassName
                  )}
                >
                  <RightElement
                    color={errorMessage && !isFocusWithin ? '#f87171' : null}
                  />
                </button>
              ) : null}
            </div>
          </FocusRing>

          {shouldShowHint && (
            <TextFieldHint
              id={hintId}
              hint={hint}
              errorText={errorMessage && !isFocusWithin ? errorMessage : ''}
              hintClassName={hintClassName}
            />
          )}
        </section>
      </div>
    );
  }
);

TextFieldBase.displayName = 'TextFieldBase';

export { TextFieldBase };
